<script setup lang="ts">
/**
 * The blocking-error list, opened from the Save button's count badge.
 *
 * Opens on hover or focus of the disabled Save button, and on click: a disabled
 * button gives no other clue why it will not save, and hover alone would be
 * unreachable by keyboard and touch.
 *
 * Resolved findings stay in the list, struck through and marked done, so the
 * user can see progress. The list itself is rebuilt on each Save press.
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import {
  AlertTriangleIcon,
  CheckIcon,
  XCloseIcon,
} from "@gohighlevel/ghl-icons/24/outline";
import { useI18n } from "vue-i18n";
import { useBuilderValidation } from "@/composition/useBuilderValidation";
import { errorAction, errorMessage } from "@/validation/messages";
import type { RuleResult } from "@/validation";

const props = defineProps<{ show: boolean }>();
/**
 * Pointer events are declared rather than left to attribute fallthrough: the
 * component's root is a Teleport, which cannot receive inherited listeners, so
 * the parent's hover handling would otherwise never fire and the popover would
 * vanish the moment the pointer left the Save button.
 */
const emit = defineEmits<{
  (event: "update:show", value: boolean): void;
  (event: "pointer-enter"): void;
  (event: "pointer-leave"): void;
}>();

const { t } = useI18n();
const { errors, errorCount, isResolved, focusResult, revalidate } =
  useBuilderValidation();

const allResolved = computed(() => errorCount.value === 0);

const heading = computed(() =>
  allResolved.value
    ? t("builderValidation.ui.allClearTitle")
    : t("builderValidation.ui.actionRequired")
);

// One string covers both counts: the copy reads "error(s)" rather than
// branching on the number.
const summary = computed(() =>
  allResolved.value
    ? t("builderValidation.ui.allClearBody")
    : t("builderValidation.ui.errorSummary", { n: errorCount.value })
);

const messageFor = (result: RuleResult): string => errorMessage(result);

const actionFor = (result: RuleResult): string => errorAction(result);

/**
 * Jumping to a finding closes the list — the user is being sent somewhere to
 * fix it, and a popover left open would cover the sidebar they land in.
 */
const jumpTo = (result: RuleResult): void => {
  if (isResolved(result)) return;
  focusResult(result);
  emit("update:show", false);
};

const close = (): void => emit("update:show", false);

/**
 * Anchored to the Save button but rendered at the end of `body`.
 *
 * The builder's styles drawer teleports into the canvas container at naive-ui's
 * own z-index, which is higher than the header's — so anything nested inside the
 * header is painted over however high its own z-index goes. Leaving the header's
 * stacking context is the only placement that holds, and the position is then
 * measured from the trigger rather than inherited from it.
 */
const anchor = ref({ top: "0px", right: "0px" });

const measureAnchor = (): void => {
  const trigger = document.querySelector("#save-action");
  if (!trigger) return;
  const rect = trigger.getBoundingClientRect();
  anchor.value = {
    top: `${Math.round(rect.bottom + 8)}px`,
    right: `${Math.round(window.innerWidth - rect.right)}px`,
  };
};

/**
 * Dismisses on a click anywhere else. Hover opens this, so without it an
 * accidental pass over the Save button leaves the list pinned open. The Save
 * controls are excluded so their own toggle is not undone by this firing first.
 */
const POPOVER_SELECTOR = "#builder-error-popover";
const TRIGGER_SELECTOR = "#save-action, #builder-save-error-trigger";

const handleOutsideClick = (event: MouseEvent): void => {
  const node = event.target as HTMLElement | null;
  if (!node?.closest) return;
  if (node.closest(POPOVER_SELECTOR) || node.closest(TRIGGER_SELECTOR)) return;
  close();
};

const stopWatchingOutsideClicks = (): void =>
  document.removeEventListener("click", handleOutsideClick);

watch(
  () => props.show,
  async (open) => {
    stopWatchingOutsideClicks();
    if (!open) return;
    measureAnchor();
    await nextTick();
    document.addEventListener("click", handleOutsideClick);
  }
);

onBeforeUnmount(stopWatchingOutsideClicks);

// Re-check as the list opens so anything fixed since the run shows as done.
watch(
  () => props.show,
  (open) => {
    if (open) revalidate();
  },
  { immediate: true }
);
</script>

<template>
  <Teleport to="body">
    <div
      v-if="props.show"
      id="builder-error-popover"
      class="builder-error-popover"
      :style="{ top: anchor.top, right: anchor.right }"
      role="dialog"
      :aria-label="heading"
      @mouseenter="emit('pointer-enter')"
      @mouseleave="emit('pointer-leave')"
      @focusin="emit('pointer-enter')"
      @focusout="emit('pointer-leave')"
    >
      <div class="popover-head">
        <CheckIcon v-if="allResolved" class="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
        <AlertTriangleIcon v-else class="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
        <div class="min-w-0 flex-1">
          <p class="popover-title">{{ heading }}</p>
          <p class="popover-summary">{{ summary }}</p>
        </div>
        <button
          id="builder-error-popover-close"
          type="button"
          class="popover-close"
          :aria-label="t('common.close')"
          @click="close"
        >
          <XCloseIcon class="h-4 w-4" />
        </button>
      </div>

      <ul id="builder-error-list" class="popover-list">
        <li
          v-for="(result, position) in errors"
          :key="result.key"
          class="popover-item"
          :class="{ 'is-resolved': isResolved(result) }"
          data-testid="builder-error-item"
        >
          <span class="item-index" aria-hidden="true">
            <CheckIcon v-if="isResolved(result)" class="h-3 w-3" />
            <template v-else>{{ position + 1 }}</template>
          </span>
          <div class="min-w-0 flex-1">
            <p class="item-message">{{ messageFor(result) }}</p>
            <button
              v-if="!isResolved(result)"
              type="button"
              class="item-action"
              @click="jumpTo(result)"
            >
              {{ actionFor(result) }}
            </button>
          </div>
        </li>
      </ul>
    </div>
  </Teleport>
</template>

<style scoped>
.builder-error-popover {
  position: fixed;
  /* Above the styles drawer, which sits at naive-ui's own stacking level. */
  z-index: 2100;
  width: 448px;
  max-width: calc(100vw - 32px);
  background: #fff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  box-shadow:
    0 8px 8px -4px rgb(16 24 40 / 3%),
    0 20px 24px -4px rgb(16 24 40 / 8%);
  text-align: left;
}
.popover-head {
  display: flex;
  gap: 12px;
  padding: 16px;
}
.popover-title {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: #101828;
}
.popover-summary {
  margin: 2px 0 0;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
}
/* See RecommendationsPanel: Bootstrap re-enables the native button appearance,
   and only that needs switching off. */
.builder-error-popover button {
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
}
.popover-close {
  background: none;
  border: none;
  padding: 0;
  color: #667085;
  align-self: flex-start;
}
.popover-close:hover {
  color: #101828;
}
/* The list scrolls rather than the popover growing: resolved items stay
   visible, so a long run would otherwise push the page. */
.popover-list {
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 320px;
  overflow-y: auto;
  border-top: 1px solid #eaecf0;
}
.popover-item {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #f2f4f7;
}
.popover-item:last-child {
  border-bottom: 0;
}
.item-index {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 9999px;
  background: #f2f4f7;
  color: #475467;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
}
.is-resolved .item-index {
  background: #dcfae6;
  color: #079455;
}
.item-message {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: #101828;
}
.is-resolved .item-message {
  color: #667085;
  text-decoration: line-through;
}
.item-action {
  margin-top: 2px;
  padding: 0;
  background: none;
  border: none;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: #004eeb;
}
.item-action:hover {
  text-decoration: underline;
}
</style>
